import type { OnInit } from '@angular/core';
import { Component, inject } from '@angular/core';
import { Router } from '@angular/router';

import {
  BadgeComponent, ButtonComponent, CardComponent, SpinnerComponent, ToastService,
} from '@echospaces-web-monorepo/es-shared-ui';
import { UpgradeService } from '@echospaces-web-monorepo/es-portal-ecms';

interface AdminCard {
  id: string;
  icon: string;
  title: string;
  description: string;
  route: string;
  available: boolean;
}

const CARDS: AdminCard[] = [
  { id: 'members',  icon: '👥', title: 'Members',     description: 'Add, promote, demote, or remove members of your organization.', route: '/admin/members',  available: true },
  { id: 'upgrades', icon: '⬆',  title: 'Updates',     description: 'Approve schema upgrades from the platform reconciler.',        route: '/admin/upgrades', available: true },
  { id: 'branding', icon: '🎨', title: 'Branding',    description: 'Override colors, logos, and theme tokens for your tenant.',    route: '/admin/branding', available: false },
  { id: 'audit',    icon: '📜', title: 'Audit log',   description: 'Who-changed-what audit trail across your organization.',       route: '/admin/audit-log',available: false },
  { id: 'billing',  icon: '💳', title: 'Billing',     description: 'Plan, invoices, and usage for your subscription.',             route: '/admin/billing',  available: false },
];

/**
 * Landing page for the Super-Admin section. Leads with the tenant's
 * database schema-version posture (current vs latest, with a one-click
 * "initiate upgrade") and follows with the admin-tool card grid; cards
 * for not-yet-implemented features are visible (so the roadmap is
 * transparent) but disabled with a "soon" badge.
 */
@Component({
  selector: 'es-admin-overview-page',
  imports: [CardComponent, BadgeComponent, ButtonComponent, SpinnerComponent],
  template: `
    <div class="max-w-5xl mx-auto">
      <div class="mb-6">
        <h1 class="text-2xl font-bold font-exo">Admin</h1>
        <p class="text-sm text-[var(--text-secondary)]">Organization-level tools for super-admins.</p>
      </div>

      <!-- Schema-version posture ─────────────────────────────── -->
      <es-card class="mb-4 block">
        <div class="flex items-start gap-3">
          <span class="text-2xl shrink-0">🗄</span>
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2 flex-wrap">
              <h3 class="font-semibold">Database schema version</h3>
              @if (upgrades.versionStatus(); as vs) {
                @if (vs.upToDate) {
                  <es-badge label="up to date" variant="success"></es-badge>
                } @else if (vs.pendingUpgrade) {
                  <es-badge label="upgrade pending approval" variant="warning"></es-badge>
                } @else {
                  <es-badge label="update available" variant="warning"></es-badge>
                }
              }
            </div>

            @if (upgrades.versionLoading() && !upgrades.versionStatus()) {
              <div class="mt-2"><es-spinner size="sm" label="Checking for updates…"></es-spinner></div>
            } @else if (upgrades.versionStatus(); as vs) {
              <p class="text-sm text-[var(--text-secondary)] mt-1">
                Current: <span class="font-medium text-[var(--text)]">{{ displayVersion(vs.currentVersion, vs.currentSchemaVersion) }}</span>
                @if (!vs.upToDate) {
                  <span class="mx-1">→</span>
                  latest: <span class="font-medium text-[var(--text)]">{{ displayVersion(vs.latestVersion, vs.latestSchemaVersion) }}</span>
                }
              </p>

              @if (!vs.upToDate) {
                <div class="mt-3">
                  @if (vs.pendingUpgrade) {
                    <p class="text-xs text-[var(--text-secondary)] mb-2">
                      An upgrade to {{ displayVersion(vs.latestVersion, vs.latestSchemaVersion) }} is waiting for a super-admin to approve it.
                    </p>
                    <es-button variant="secondary" size="sm" (clicked)="goToApproval()">Review in Settings</es-button>
                  } @else {
                    <es-button
                      variant="primary"
                      size="sm"
                      [loading]="upgrades.initiating()"
                      [disabled]="upgrades.initiating()"
                      (clicked)="initiate()"
                    >Initiate upgrade</es-button>
                  }
                </div>
              }
            } @else {
              <p class="text-sm text-[var(--text-secondary)] mt-1">
                {{ upgrades.versionError() ? "Couldn't reach the update service — this is usually temporary." : 'Version information is unavailable right now.' }}
              </p>
              <es-button class="mt-2 inline-block" variant="secondary" size="sm" (clicked)="upgrades.loadVersionStatus()">Retry</es-button>
            }
          </div>
        </div>
      </es-card>

      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        @for (card of cards; track card.id) {
          <es-card
            [clickable]="card.available"
            (clicked)="card.available && router.navigate([card.route])"
          >
            <div class="flex items-start gap-3">
              <span class="text-2xl shrink-0">{{ card.icon }}</span>
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2 flex-wrap">
                  <h3 class="font-semibold">{{ card.title }}</h3>
                  @if (!card.available) {
                    <es-badge label="soon" variant="secondary"></es-badge>
                  }
                </div>
                <p class="text-xs text-[var(--text-secondary)] mt-1">{{ card.description }}</p>
              </div>
            </div>
          </es-card>
        }
      </div>
    </div>
  `,
})
export class AdminOverviewPageComponent implements OnInit {
  readonly router = inject(Router);
  readonly upgrades = inject(UpgradeService);
  private readonly toast = inject(ToastService);

  readonly cards = CARDS;

  ngOnInit(): void {
    this.upgrades.loadVersionStatus();
  }

  /** Prefer the customer-facing release label; fall back to the raw schema timestamp. */
  displayVersion(version: string | null, schemaVersion: string | null): string {
    return version ?? schemaVersion ?? 'Not set';
  }

  /** Request the upgrade — creates a pending offer the super-admin then approves in Settings. */
  async initiate(): Promise<void> {
    try {
      await this.upgrades.initiate();
      this.toast.success('Upgrade requested — approve it in Settings to start.');
    } catch {
      this.toast.error('Failed to initiate the upgrade.');
    }
  }

  goToApproval(): void {
    this.router.navigate(['/settings']);
  }
}
